import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { UserRoleEnum } from 'src/app/common/services/auth/enums/user-role.enum';
import { StatusEnum } from 'src/app/common/utils/status-enum.models';
import { ButtonColorEnum } from 'src/app/shared/buttons/enums/button-color.enum';
import { InputTypeEnum } from 'src/app/shared/input/enums/input-type.enum';
import { MaskTypeEnum } from 'src/app/shared/input/enums/mask-type.enum';
import {
  GetClientRequest,
  GetClientsResponse,
} from '../../client/models/client.model';
import { ClientService } from '../../client/service/client.service';
import {
  GetUserRequest,
  GetUserResponse,
} from '../../user/service/models/user.service.model';
import { UserService } from '../../user/service/user.service';
import { CategoryService } from '../service/category.service';
import {
  Keyword,
  KeywordVariation,
  UpdateCategoryRequest,
} from '../service/models/category.service.model';

const MAX_WORDS_TO_SHOW = 5;

@Component({
  selector: 'app-category-details',
  templateUrl: './category-details.component.html',
  styleUrls: ['./category-details.component.scss'],
})
export class CategoryDetailsComponent implements OnInit {
  originalKeywords: Keyword[] = [];
  keywords: string[] = [];
  textareaContent: string = '';
  showAllKeywords: boolean = false;
  showAllVariations: boolean = false;
  isLoading: boolean = false;
  success: boolean = false;
  error: boolean = false;
  categoryId: string = '';
  activeOptions: string[] = ['Ativo', 'Inativo'];

  selectedKeyword: Keyword | undefined | null = null;
  selectedStatus: string[] = [];

  sendEmailAlertOptions: string[] = ['Sim', 'Não'];
  receiversOptions: string[] = [];
  loadingReceivers: boolean = false;
  selectedReceivers: string[] = [];
  originalReceivers: GetUserResponse[] = [];
  loadingSearchReceivers: boolean = false;
  totalItemsReceivers: number = 0;
  loadingMoreReceivers: boolean = false;

  usersFilter: GetUserRequest = {
    pageSize: 10,
    pageNumber: 1,
    profiles: [UserRoleEnum.supervisor],
    sortAscending: true,
    sortBy: 'Name',
  } as GetUserRequest;

  clientOptions: string[] = [];
  loadingClients: boolean = false;
  originalClients: GetClientsResponse[] = [];
  loadingSearchClients: boolean = false;
  totalItemsClients: number = 0;
  loadingMoreClients: boolean = false;
  selectedClients: string[] = [];

  clientsFilter: GetClientRequest = {
    pageSize: 10,
    pageNumber: 1,
    status: [StatusEnum.ATIVO],
    sortAscending: true,
    sortBy: 'Name',
  } as GetClientRequest;

  loadingCategory = false;

  form = this.formBuilder.group({
    name: ['', Validators.required],
    color: ['', Validators.required],
    active: ['', Validators.required],
    keyword: [''],
    variation: [''],
    client: ['', Validators.required],
    sendEmailAlert: ['', Validators.required],
    usersEmailAlert: [['']],
  });

  ButtonColorEnum = ButtonColorEnum;
  MaskTypeEnum = MaskTypeEnum;
  InputTypeEnum = InputTypeEnum;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly categoryService: CategoryService,
    private readonly clientService: ClientService,
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly toastrService: ToastrService
  ) {}

  ngOnInit() {
    this.route.params.subscribe((params) => {
      const categoryIdFromUrl = params['id'];
      if (categoryIdFromUrl) this.categoryId = categoryIdFromUrl;
    });

    this.loadingCategory = true;

    this.getClients(null);
    this.getReceivers(null);
    this.getCategory();
  }

  getClients(name: string | null) {
    this.clientsFilter.pageNumber = 1;

    if (name) {
      this.clientsFilter.name = name;
      this.loadingSearchClients = true;
    } else {
      this.loadingClients = true;
      this.clientsFilter.name = null;
    }

    this.clientService.get(this.clientsFilter).subscribe({
      next: (data) => {
        this.originalClients = data.items;
        this.clientOptions = data.items.map((i) => i.name + ' | ' + i.cnpj);
        this.totalItemsClients = data.totalItems;
        this.loadingClients = false;
        this.loadingSearchClients = false;
      },
      error: () => {
        this.toastrService.error('Não foi possível carregar os clientes.');
        this.loadingClients = false;
        this.loadingSearchClients = false;
      },
    });
  }

  getMoreClients() {
    if (this.totalItemsClients != this.clientOptions.length) {
      this.loadingMoreClients = true;
      if (this.clientsFilter.pageNumber) this.clientsFilter.pageNumber += 1;

      this.clientService.get(this.clientsFilter).subscribe({
        next: (data) => {
          this.originalClients = this.originalClients.concat(data.items);
          this.totalItemsClients = data.totalItems;

          this.clientOptions = this.clientOptions.concat(
            data.items.map((i) => i.name + ' | ' + i.cnpj)
          );

          this.loadingMoreClients = false;
        },
        error: () => {
          this.toastrService.error('Não foi possível carregar os clientes.');
          this.loadingMoreClients = false;
        },
      });
    }
  }

  getReceivers(name: string | null) {
    this.usersFilter.pageNumber = 1;

    if (name) {
      this.usersFilter.name = name;
      this.loadingSearchReceivers = true;
    } else {
      this.loadingReceivers = true;
      this.usersFilter.name = null;
    }

    this.userService.get(this.usersFilter).subscribe({
      next: (data) => {
        this.originalReceivers = data.items;
        this.receiversOptions = data.items.map((i) => i.name);
        this.totalItemsReceivers = data.totalItems;
        this.loadingReceivers = false;
        this.loadingSearchReceivers = false;
      },
      error: () => {
        this.toastrService.error(
          'Não foi possível carregar a lista de recebimento.'
        );
        this.loadingReceivers = false;
        this.loadingSearchReceivers = false;
      },
    });
  }

  getMoreReceivers() {
    if (this.totalItemsReceivers != this.receiversOptions.length) {
      this.loadingMoreReceivers = true;
      if (this.usersFilter.pageNumber) this.usersFilter.pageNumber += 1;

      this.userService.get(this.usersFilter).subscribe({
        next: (data) => {
          this.originalReceivers = this.originalReceivers.concat(data.items);
          this.totalItemsReceivers = data.totalItems;

          this.receiversOptions = this.receiversOptions.concat(
            data.items.map((i) => i.name)
          );

          this.loadingMoreReceivers = false;
        },
        error: () => {
          this.toastrService.error(
            'Não foi possível carregar a lista de recebimento.'
          );
          this.loadingMoreReceivers = false;
        },
      });
    }
  }

  private getCategory() {
    this.categoryService.getById(this.categoryId).subscribe((category) => {
      if (category) {
        this.form.patchValue({
          name: category.name,
          active: category.active ? 'Ativo' : 'Inativo',
          color: category.color,
          client: category.client
            ? category.name + ' (' + category.client.cnpj + ')'
            : null,
          sendEmailAlert: category.sendEmailAlert ? 'Sim' : 'Não',
        });

        this.selectedClients = category.client
          ? [category.name + ' (' + category.client.cnpj + ')']
          : [];

        if (category.keywords.length > 0)
          this.selectedKeyword = category.keywords[0];

        this.selectedStatus = category.active ? ['Ativo'] : ['Inativo'];
        this.keywords = [];
        const keywords = category.keywords.map((keyword) => keyword.name);

        if (category.usersEmailAlert) {
          this.selectedReceivers = category.usersEmailAlert
            .map((userAlert) => {
              const receiver = this.originalReceivers.find(
                (receiver) => receiver.id === userAlert
              );
              return receiver ? receiver.name : '';
            })
            .filter((name) => name !== '');
        }

        this.originalKeywords = category.keywords;
        this.keywords.push(...keywords);
        this.form.controls.keyword.reset();
        this.updateKeywordTextarea();
        this.loadingCategory = false;
      }
    });
  }

  closeModal() {
    this.form.reset();
    this.router.navigate(['/category/']);
  }

  isKeywordValid() {
    var allCategoryWords = this.originalKeywords
      .map((k) => k.keywordVariations)
      .flat()
      .map((x) => x.name);
    allCategoryWords.push(...this.keywords);

    return (
      this.form.controls.keyword.valid &&
      !this.stringExistsInList(
        this.form.controls.keyword.value ?? '',
        allCategoryWords
      )
    );
  }

  isKeywordVariationValid() {
    var allCategoryWords = this.originalKeywords
      .map((k) => k.keywordVariations)
      .flat()
      .map((x) => x.name);
    allCategoryWords.push(...this.keywords);

    return (
      this.form.controls.variation.valid &&
      !this.stringExistsInList(
        this.form.controls.variation.value ?? '',
        allCategoryWords
      )
    );
  }

  addKeyword() {
    const newKeyword = this.form.controls.keyword.value ?? '';

    var allCategoryWords = this.originalKeywords
      .map((k) => k.keywordVariations)
      .flat()
      .map((x) => x.name);
    allCategoryWords.push(...this.keywords);

    if (
      newKeyword.trim() !== '' &&
      !this.stringExistsInList(newKeyword, allCategoryWords)
    ) {
      this.keywords.push(newKeyword);
      this.form.controls.keyword.reset();
      this.updateKeywordTextarea();
      this.originalKeywords.push({
        name: newKeyword,
        active: true,
        keywordVariations: [],
      } as Keyword);
    }
  }

  addKeywordVariation() {
    const newVariation = this.form.controls.variation.value ?? '';

    var allCategoryWords = this.originalKeywords
      .map((k) => k.keywordVariations)
      .flat()
      .map((x) => x.name);
    allCategoryWords.push(...this.keywords);

    if (
      newVariation.trim() !== '' &&
      !this.stringExistsInList(newVariation, allCategoryWords)
    ) {
      this.selectedKeyword?.keywordVariations.push({
        name: newVariation,
        active: true,
      } as KeywordVariation);
      this.form.controls.variation.reset();
    }
  }

  showSeeAllKeywordsButton(): boolean {
    return !this.showAllKeywords && this.keywords.length > MAX_WORDS_TO_SHOW;
  }

  showSeeAllVariationsButton(): boolean | null | undefined {
    return (
      !this.showAllVariations &&
      this.selectedKeyword &&
      this.selectedKeyword.keywordVariations.length > MAX_WORDS_TO_SHOW
    );
  }

  toggleShowAllKeywords(): void {
    this.showAllKeywords = !this.showAllKeywords;
  }

  toggleShowAllVariations(): void {
    this.showAllVariations = !this.showAllVariations;
  }

  visibleKeywords(): string[] {
    if (this.showAllKeywords) return this.keywords;
    else return this.keywords.slice(0, MAX_WORDS_TO_SHOW);
  }

  visibleVariations(): KeywordVariation[] | null | undefined {
    if (this.showAllVariations) return this.selectedKeyword?.keywordVariations;
    else
      return this.selectedKeyword?.keywordVariations.slice(
        0,
        MAX_WORDS_TO_SHOW
      );
  }

  updateKeywordTextarea() {
    if (this.keywords.length <= MAX_WORDS_TO_SHOW || this.showAllKeywords)
      this.textareaContent = this.keywords.join(' ');
  }

  removeKeyword(index: number) {
    var keyword = this.keywords.splice(index, 1)[0];
    this.updateKeywordTextarea();

    this.originalKeywords = this.originalKeywords.filter(
      (k) => k.name != keyword
    );

    if (keyword == this.selectedKeyword?.name) this.selectedKeyword = null;

    if (this.keywords.length <= MAX_WORDS_TO_SHOW) this.showAllKeywords = false;
  }

  removeKeywordVariation(index: number) {
    var keyword = this.originalKeywords.find(
      (keyword) => keyword.name == this.selectedKeyword?.name
    );

    keyword?.keywordVariations.splice(index, 1);
  }

  isFormComplete() {
    return this.form.valid && this.keywords.length > 0;
  }

  getKeywordsFromText(keywordTexts: string[]): Keyword[] {
    var response: Keyword[] = [];

    keywordTexts.forEach((keywordText) => {
      var keyword = this.originalKeywords.find(
        (originalKeyword) => originalKeyword.name === keywordText
      );
      if (keyword) response.push(keyword);
      else {
        var newKeywork: Keyword = {
          active: true,
          name: keywordText,
          keywordVariations: [],
        };
        response.push(newKeywork);
      }
    });

    return response;
  }

  updateCategory() {
    if (this.isLoading) return;

    this.isLoading = true;
    this.error = false;

    var keywords = this.getKeywordsFromText(this.keywords);
    var usersId = this.getUsersIdFromText(
      this.form.controls.usersEmailAlert.value ?? []
    );

    var request = {
      name: this.form.controls.name.value ?? '',
      color: this.form.controls.color.value ?? '',
      clientId: this.getClientIdFromText(this.form.controls.client.value ?? ''),
      sendEmailAlert:
        this.form.controls.sendEmailAlert.value === 'Sim' ? true : false,
      usersEmailAlert: usersId,
      active: this.form.controls.active.value === 'Ativo' ? true : false,
      keywords: keywords,
    } as UpdateCategoryRequest;

    this.categoryService.update(this.categoryId, request).subscribe({
      next: () => {
        this.isLoading = false;
        this.toastrService.success('Categoria alterada com sucesso!');
        this.router.navigate(['/category']);
      },
      error: (e) => {
        this.isLoading = false;
        this.error = true;
        this.toastrService.error(
          e.error ?? 'Ocorreu um erro ao editar a categoria.'
        );
      },
    });
  }

  getUsersIdFromText(users: string[]): string[] | null {
    if (!users || users.length === 0) return null;
    let usersId: string[] = [];
    users.forEach((name) => {
      let attendant = this.originalReceivers.find((i) => i.name === name);
      if (attendant) usersId.push(attendant.id);
    });

    return usersId ? usersId : null;
  }

  setSelectedKeyword(keywordName: string) {
    var keyword = this.originalKeywords.find(
      (keyword) => keyword.name == keywordName
    );

    this.selectedKeyword = keyword;
  }

  normalizeString(str: string): string {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  stringExistsInList(target: string, list: string[]): boolean {
    const normalizedTarget = this.normalizeString(target);
    return list.some((item) => this.normalizeString(item) === normalizedTarget);
  }

  getClientIdFromText(text: string): string {
    if (!text) return '';
    let clientName = text.split('|')[0].trim();
    let client = this.originalClients.find(
      (i) => i.name.trim() === clientName.trim()
    );
    if (client) return client.id;
    else return '';
  }
}
